'use client';

import Link from 'next/link';
import type { ReactNode } from 'react';

type SettingsLinkProps = {
  /** Route to navigate to. Client-side, via Next `Link`. */
  href: string;
  children: ReactNode;
  'data-testid'?: string;
  className?: string;
  /** Set with `rel="noopener noreferrer"` for a link that opens elsewhere (e.g. `_blank`). */
  target?: '_blank';
  rel?: string;
  /**
   * Draws the link in the row's own text colour instead of link blue (a name in a table, where
   * Figma draws no blue). It still shows the link colour on hover and stays a real anchor.
   */
  asText?: boolean;
};

/**
 * A link inside a converted settings surface.
 *
 * BOTH UTILITIES CARRY `!`, and neither is decorative.
 *
 * Tailwind's preflight is deliberately not imported (see the header of
 * src/app/design-system.css), so Bootstrap's reboot is what styles a bare anchor
 * in this app — measured from a compile of the app's Sass:
 *
 *     a       { color: rgba(var(--bs-link-color-rgb), …); text-decoration: underline }
 *     a:hover { text-decoration: underline }
 *
 * Those declarations are UNLAYERED, and for normal declarations an unlayered
 * rule outranks every layered one — so a plain `text-text-link` is served,
 * matches, and still loses: the link renders Bootstrap blue (#0d6efd) and
 * underlined. For IMPORTANT declarations the order inverts, a layer beating the
 * unlayered anonymous layer, so an important utility wins. That is the same
 * remedy `ListingTable`'s overflow pair uses, and the reason the tab strip
 * zeroes its borders one side at a time.
 *
 * `no-underline` needs no collision guard of its own — Bootstrap's equivalent is
 * `.text-decoration-none`, a different name — but it still needs the modifier,
 * because what it is beating is reboot's bare-element rule rather than a
 * same-named utility.
 *
 * Section 3 of src/app/design-system-compat.css handles this for the breadcrumb
 * and for icon-coloured anchors by reverting the layer instead; that route needs
 * a new gated selector per anchor family, and this one does not.
 */
export const SettingsLink = ({
  href,
  children,
  'data-testid': dataTestId,
  className,
  target,
  rel,
  asText = false,
}: SettingsLinkProps) => (
  <Link
    href={href}
    target={target}
    rel={rel}
    data-testid={dataTestId}
    // The space before the interpolation is defensive, not a repair. Tailwind
    // reads this file as text and does not extract a utility written flush
    // against `${` — but this particular one survives either way, because the
    // design system's own bundle is a scanned source and references
    // `hover:text-text-link-hover` three times, so the class is generated from
    // there. Measured both ways: the rule count is identical.
    //
    // It is written with the space anyway, because relying on a second
    // occurrence in someone else's package is not a property this file controls.
    // `bg-bg-surface!` in SettingsContentPane is the same shape with no such
    // backstop, and it silently generated nothing.
    className={`${asText ? 'text-text-primary!' : 'text-text-link!'} no-underline! hover:text-text-link-hover! ${className ?? ''}`}
  >
    {children}
  </Link>
);

export default SettingsLink;
